import React, { useState } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import Icon from 'src/components/ui/Icon/Icon';
import TopBar from 'src/components/ui/TopBar/TopBar';
import Divider from 'src/components/ui/Divider/Divider';
import { ControlledInput } from 'src/components/ui/Input/Input';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { $publicApi } from 'src/services/api';
import logger from 'src/utils/logger';
import styles from '../styles/LoginAndRegister.module.scss';
import { EmailFormValues, emailSchema } from './schema';
import { extractBackendErrorMessage } from './errorUtils';

const RegisterEmail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const { control, handleSubmit, formState } = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' },
    mode: 'onSubmit',
  });

  const { mutateAsync, isPending } = $publicApi.useMutation('post', '/registrations/');

  const onSubmit = async (values: EmailFormValues) => {
    try {
      await mutateAsync({ body: { email: values.email } });
      setSubmittedEmail(values.email);
    } catch (err) {
      logger.error('Failed to create registration', err);
      const backendMessage = extractBackendErrorMessage(err);
      toast.error(backendMessage || t('registration.errors.create_failed'));
    }
  };

  return (
    <div className={classNames(styles.root)}>
      <div className={styles.topbar}>
        <TopBar onClick={() => navigate('/login')} />
      </div>
      <div className={styles.main}>
        <div className={styles.content}>
          <div className="relative w-full px-20 md:px-64 py-32 md:py-48 flex-1 flex flex-col">
            {submittedEmail ? (
              <div className="flex flex-col">
                <div className="mb-32">
                  <h2 className="typo-title-36 text-full mb-12">{t('registration.email_sent_title')}</h2>
                  <span className="typo-title-24 text-faded">
                    {t('registration.email_sent_copy', { email: submittedEmail })}
                  </span>
                </div>
                <Divider customColor="border-subtle" className="mb-32" />
                <p className="typo-text-14 text-faded mb-32">{t('registration.email_sent_hint')}</p>
                <Button
                  title={t('onboarding.log_in')}
                  tone={ButtonToneProps.SECONDARY}
                  onClick={() => navigate('/login')}
                />
              </div>
            ) : (
              <>
                <form onSubmit={handleSubmit(onSubmit)} className="mt-8">
                  <div className="mb-32">
                    <h2 className="typo-title-36 text-full mb-12">{t('onboarding.register_account')}</h2>
                    <span className="typo-title-24 text-faded">{t('onboarding.register_copy')}</span>
                  </div>
                  <Divider customColor="border-subtle" className="mb-32" />
                  <div className="flex flex-col mb-24">
                    <ControlledInput
                      control={control}
                      name="email"
                      label={t('general.email')}
                      className="w-full"
                      placeholder={t('registration.placeholders.email')}
                      type="email"
                      autoComplete="email"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Button
                      title={t('onboarding.get_started')}
                      tone={ButtonToneProps.PRIMARY_BLUE}
                      type="submit"
                      disabled={isPending || formState.isSubmitting}
                    />
                  </div>
                </form>

                <div className="my-32 flex items-center">
                  <span className={styles.line} />
                  <span className="mx-8 text-faded typo-text-14">{t('onboarding.or')}</span>
                  <span className={styles.line} />
                </div>
                <Button
                  title={t('onboarding.log_in')}
                  tone={ButtonToneProps.SECONDARY}
                  onClick={() => navigate('/login')}
                />
              </>
            )}

            <span className="w-full inline-block typo-text-14 text-faded text-center mt-auto pt-32">
              {new Date().getFullYear()} {t('general.all_rights_reserved')}
            </span>
          </div>
        </div>
        <div className={classNames(styles.image, 'md:w-1/2 flex justify-center items-center')}>
          <div className={styles.hero} aria-hidden />
          <Icon className={styles.icon} name="logo-full-white" width={91} height={30} />
        </div>
      </div>
    </div>
  );
};

export default RegisterEmail;
